import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CoursesPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
  ButtonWrapper,
  ButtonsContainer,
} from "./CoursesPage.styles";
import Sidebar from "../../components/Sidebar";
import Table, { TableText } from "../../components/Table";
import {
  Course,
  CourseResponse,
  FilterOption,
} from "../../services/courseService/courseService.types";
import {
  fetchCourses,
  softDeleteCourse,
  updateCourse,
  restoreCourse,
  createCourse,
  updateCourseById,
  fetchCategories,
} from "../../services/courseService/courseService";
import Button from "../../components/Buttons/Button";
import LessonsButton from "../../components/Buttons/LessonsButton";
import IconButton from "../../components/Buttons/IconButton";
import StatusBadge from "../../components/StatusBadge";
import DeletedRow from "../../components/DeletedRow";
import RestoredRow from "../../components/RestoredRow";
import { ConfirmModal, CreateModal } from "../../components/Modals";
import { CoursesPageProps } from "./CoursesPage.types";
import { formatPrice } from "../../utils/formatNumber";
import { useSubscription } from '../../hooks/useSubscription';
import { formatServerErrors } from "../../utils/errorHandler";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { getServerErrorData } from "utils/httpError";
import { getCurrentUserData } from "../../services/settingsService/settingsService";
import { hasRequiredCourseCreationRequisites } from "../../utils/courseCreation";
import { delIcon, editIcon, publishIcon } from "assets/icons";

const CoursesPage: React.FC<CoursesPageProps> = ({ viewMode }) => {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [categoryFilterOptions, setCategoryFilterOptions] = useState<FilterOption[]>([]);
  const filterKey = JSON.stringify(filters);

  const {
    items: courses,
    setItems: setCourses,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload: loadCourses,
  } = useListPage<Course, CourseResponse>({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      fetchCourses(page, size, orderBy, {
        categories: filters.category ?? [],
      }),
    select: (response) => ({
      items: response.results,
      total: response.count,
    }),
    onLoaded: (response) => {
      setCategoryFilterOptions(response.filter_options?.categories ?? []);
    },
    errorMessage: "Ошибка загрузки курсов",
    errorStateMessage: "Не удалось загрузить курсы.",
    reloadKey: filterKey,
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCoursePrerequisitesModalOpen, setIsCoursePrerequisitesModalOpen] = useState(false);
  const [isCheckingCoursePrerequisites, setIsCheckingCoursePrerequisites] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [courseToEdit, setCourseToEdit] = useState<Course | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const toast = useNotify();
  const navigate = useNavigate();
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();

  const handleFilterSave = (key: string, selected: string[]) => {
    setFilters((previous) => ({ ...previous, [key]: selected }));
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleFilterReset = (key: string) => {
    setFilters((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const openEditModal = (course: Course) => {
    setCourseToEdit(course);
    setIsCreateModalOpen(true);
  };

  const handleEditCourse = async (formData: Record<string, string | File[] | null>) => {
    if (!courseToEdit) return;

    setExternalErrors({});
    try {
      const courseData: Partial<Course> = {
        title: formData["courseName"] as string,
        category: formData["category"] as string,
        price: formData["price"] as string,
        description: formData["description"] as string,
      };
      await updateCourseById(courseToEdit.id, courseData);

      setRecentlyEdited(courseToEdit.id);

      closeCreateModal();
      loadCourses();

      setTimeout(() => setRecentlyEdited(null), 3000);
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors = formatServerErrors(serverErrors, { title: 'courseName' });

        setExternalErrors(formattedErrors);
      } else {
        logger.error("Ошибка при обновлении курса:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Ошибка обновления курса",
          life: 3000,
        });
      }
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoryList = await fetchCategories();
        setCategories(categoryList);
      } catch (error) {
        toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка загрузки категорий", life: 3000 });
        logger.error("Ошибка при загрузке категорий:", error);
      }
    };

    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDeleteModal = (course: Course) => {
    setCourseToDelete(course);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (courseToDelete) {
      setPendingDelete(courseToDelete.id);
      setIsDeleteModalOpen(false);
      setCourseToDelete(null);

      deleteTimeoutRef.current = setTimeout(async () => {
        try {
          await softDeleteCourse(courseToDelete.id);
          setPendingDelete(null);
          loadCourses();
        } catch (error) {
          toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка мягкого удаления курса", life: 3000 });
          logger.error("Ошибка при мягком удалении курса:", error);
        }
      }, 5000);
    }
  };

  const closeModal = () => {
    setIsDeleteModalOpen(false);
    setCourseToDelete(null);
  };

  const handlePublish = async (courseId: number) => {
    try {
      await updateCourse(courseId, { is_approved_by_admin: true });
      loadCourses();
    } catch (error) {
      toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка публикации курса", life: 3000 });
      logger.error("Ошибка при публикации курса:", error);
    }
  };

  const restoreCourseHandler = async (courseId: number) => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }

    try {
      await restoreCourse(courseId);
      setRecentlyRestored(courseId);
      setPendingDelete(null);

      loadCourses();

      setTimeout(() => setRecentlyRestored(null), 3000);
    } catch (error) {
      toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка восстановления курса", life: 3000 });
      logger.error("Ошибка при восстановлении курса:", error);
    }
  };

  const openCreateModal = async () => {
    if (isCheckingCoursePrerequisites) return;

    setIsCheckingCoursePrerequisites(true);
    try {
      const user = await getCurrentUserData();
      if (!hasRequiredCourseCreationRequisites(user)) {
        setIsCoursePrerequisitesModalOpen(true);
        return;
      }

      setCourseToEdit(null);
      setExternalErrors({});
      setIsCreateModalOpen(true);
    } catch (error) {
      logger.error("Ошибка проверки реквизитов перед созданием курса:", error);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось проверить реквизиты преподавателя. Попробуйте ещё раз.",
        life: 4000,
      });
    } finally {
      setIsCheckingCoursePrerequisites(false);
    }
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateCourse = async (formData: Record<string, string | File[] | null>) => {
    setExternalErrors({});
    try {
      const userId = Number(
        document.cookie.split("; ").find((row) => row.startsWith("user_id="))?.split("=")[1]
      );

      const courseData = {
        title: formData.courseName as string,
        category: formData.category as string,
        price: formData.price as string,
        description: formData.description as string,
        instructor: userId,
      };

      const createdCourse = await createCourse(courseData);

      setRecentlyCreated(createdCourse.id);

      closeCreateModal();
      loadCourses();

      setTimeout(() => setRecentlyCreated(null), 3000);
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors = formatServerErrors(serverErrors, { title: 'courseName' });

        setExternalErrors(formattedErrors);
      } else {
        logger.error("Ошибка при создании курса:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Ошибка создания курса",
          life: 3000,
        });
      }
    }
  };

  /*
    Ширины заданы явно, иначе колонки делит между собой содержимое:
    длинное название курса забирает место у колонки действий, и иконки
    редактирования и удаления срезаются по overflow ячейки.
  */
  const headers = [
    { label: "Название", key: "title", fieldName: "title", width: "30%" },
    {
      label: "Категория",
      key: "category",
      fieldName: "category",
      width: "22%",
      isFilter: true,
      filterType: "list" as const,
      listOptions:
        categoryFilterOptions.length > 0
          ? categoryFilterOptions
          : categories.map((category) => ({ label: category, value: category })),
      listFilter: {
        title: "Категория",
        searchable: true,
        searchPlaceholder: "Найти категорию",
        emptyMessage: "Категории не найдены",
      },
    },
    { label: "Цена", key: "price", fieldName: "price", width: "11%" },
    { label: "Опубликовано", key: "status", fieldName: "is_approved_by_admin", width: "12%" },
    {
      label: viewMode === "lessons" ? "Уроки" : "Домашние задания",
      key: "lessons",
      sortable: false,
      width: "13%"
    },
    { label: "", key: "buttons", sortable: false, width: "12%" },
  ];

  const rows = courses.map((course) => {
    if (course.id === pendingDelete) {
      return {
        title: (
          <DeletedRow
            id={course.id}
            onRestore={restoreCourseHandler}
            colSpan={headers.length}
            message={`Курс ${course.title} будет удален через 5 секунд.`}
            targetText="Восстановить курс"
          />
        ),
      };
    }

    return {
      title: <TableText weight={500}>{course.title}</TableText>,
      category: <TableText>{course.category}</TableText>,
      price: <TableText numeric>{`${formatPrice(course.price)} руб.`}</TableText>,
      status: (
        <StatusBadge
          text={course.is_approved_by_admin ? "ДА" : "НЕТ"}
          variant={course.is_approved_by_admin ? "green" : "red"}
        />
      ),
      lessons: course.with_lessons ? (
        <LessonsButton
          href={`/courses/${course.id}/${viewMode}`}
          text="Смотреть"
        />
      ) : (
        <LessonsButton
          href={`/courses/${course.id}/${viewMode}`}
          text="Отсутствуют"
          noLessons={true}
        />
      ),
      buttons: hasActiveSubscription ? (
        <ButtonsContainer>
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => openEditModal(course)}
          />
          <IconButton
            icon={delIcon}
            tone="danger"
            altText="Удалить"
            onClick={() => openDeleteModal(course)}
          />
          {!course.is_approved_by_admin && (
            <IconButton
              icon={publishIcon}
              altText="Опубликовать"
              tooltip="Опубликовать"
              onClick={() => handlePublish(course.id)}
            />
          )}
        </ButtonsContainer>
      ) : null,
    };
  });

  if (recentlyRestored !== null) {
    const restoredIndex = courses.findIndex((c) => c.id === recentlyRestored);
    if (restoredIndex !== -1) {
      rows.splice(restoredIndex + 1, 0, {
        title: (
          <RestoredRow
            colSpan={headers.length}
            message="Курс успешно восстановлен"
          />
        ),
      });
    }
  }

  if (recentlyCreated !== null) {
    const createdIndex = courses.findIndex((c) => c.id === recentlyCreated);
    if (createdIndex !== -1) {
      rows.splice(createdIndex + 1, 0, {
        title: (
          <RestoredRow
            colSpan={headers.length}
            message="Курс успешно создан"
          />
        ),
      });
    }
  }

  if (recentlyEdited !== null) {
    const editedIndex = courses.findIndex((c) => c.id === recentlyEdited);
    if (editedIndex !== -1) {
      rows.splice(editedIndex + 1, 0, {
        title: (
          <RestoredRow
            colSpan={headers.length}
            message="Курс успешно отредактирован"
          />
        ),
      });
    }
  }

  const rowAmountOptions = [
    { label: "7 курсов", value: 7 },
    { label: "15 курсов", value: 15 },
    { label: "20 курсов", value: 20 },
  ];
  return (
    <CoursesPageContainer>
      <Sidebar />
      <ContentWrapper>
        <HeaderContainer>
          <Title>
            {viewMode === "lessons" ? "Мои курсы" : "Домашние задания"}
          </Title>
          <ButtonWrapper>
            <Button
              onClick={() => window.open('https://rutube.ru/video/d4facb4158d5cbc72f414ec42e81c69b/', '_blank')}
              variant="accent"
              size="md"
            >
              Обзор платформы
            </Button>
            {hasActiveSubscription && (
              <Button
                onClick={() => void openCreateModal()}
                type="button"
                variant="primary"
                size="md"
                disabled={isCheckingCoursePrerequisites}
              >
                Новый курс
              </Button>
            )}
          </ButtonWrapper>
        </HeaderContainer>
        <Table
          emptyState={{
            title: "Курсов пока нет",
            description:
              "Создайте первый курс — здесь появятся его уроки и статистика.",
          }}
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onSort={handleSort}
          rowAmountOptions={rowAmountOptions}
          specialRowKey="title"
          loading={loading}
          activeFilter={activeFilter}
          handleActiveFilterChange={setActiveFilter}
          appliedFilters={filters}
          onFilterSave={handleFilterSave}
          onFilterReset={handleFilterReset}
        />
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Подтверждение"
          message={`Вы уверены, что хотите удалить этот курс?`}
          onConfirm={confirmDelete}
          onCancel={closeModal}
        />
        <ConfirmModal
          isOpen={isCoursePrerequisitesModalOpen}
          title="Заполните реквизиты"
          message="Перед созданием нового курса заполните в настройках ИНН, ОГРН/ОГРНИП и данные электронной подписи."
          confirmButtonText="Перейти в настройки"
          onConfirm={() => {
            setIsCoursePrerequisitesModalOpen(false);
            navigate("/settings");
          }}
          onCancel={() => setIsCoursePrerequisitesModalOpen(false)}
        />
        {isCreateModalOpen && (
          <CreateModal
            title={courseToEdit ? "Редактировать курс" : "Новый курс"}
            fields={[
              { name: "courseName", type: "text", placeholder: "Название курса", required: true },
              { name: "price", type: "text", placeholder: "Стоимость", required: true },
              {
                name: "category",
                type: "selectWithInput",
                placeholder: "Категория",
                options: categories,
                required: true,
              },
              { name: "description", type: "textarea", placeholder: "Описание" },
            ]}
            onSubmit={courseToEdit ? handleEditCourse : handleCreateCourse}
            submitButtonText={courseToEdit ? "Сохранить" : "Создать"}
            onCancel={closeCreateModal}
            hasDragAndDrop={false}
            initialValues={
              courseToEdit
                ? {
                  courseName: courseToEdit.title,
                  price: courseToEdit.price.toString(),
                  category: courseToEdit.category,
                  description: courseToEdit.description,
                }
                : {}
            }
            externalErrors={externalErrors}
          />
        )}
      </ContentWrapper>
    </CoursesPageContainer>
  );
};

export default CoursesPage;
